import { inject, Service } from '@angular/core';
import { MadCloudResult } from '@common/http/models/mad-cloud-result.model';
import { PageFilter } from '@common/paged-data/types/page-filter.type';
import { Page } from '@common/paged-data/types/page.type';
import { CollectionServiceBase } from '@portal-core/data/collection/services/collection.service.base';
import { LicenseStorageService } from '@portal-core/license-storage/services/license-storage.service';
import { LicenseUser } from '@portal-core/license-users/models/license-user.model';
import { PermissionsService } from '@portal-core/permissions/services/permissions.service';
import { CreateProjectTemplate } from '@portal-core/projects/create-project/models/create-project-template.model';
import { ProjectReportScanStatus } from '@portal-core/projects/enums/project-report-scan-status.enum';
import { ProjectStatus } from '@portal-core/projects/enums/project-status.enum';
import { ProjectDependencies } from '@portal-core/projects/models/project-dependencies.model';
import { ProjectReportScanStatusWithLatestNotificationId } from '@portal-core/projects/models/project-report-scan-status-with-latest-notification-id.model';
import { Project } from '@portal-core/projects/models/project.model';
import { ProjectsApiService } from '@portal-core/projects/services/projects-api.service';
import { ProjectsDataService } from '@portal-core/projects/services/projects-data.service';
import { Team } from '@portal-core/teams/models/team.model';
import { Resettable } from '@portal-core/util/resettable.decorator';
import { first, map, Observable, switchMap, tap } from 'rxjs';

@Service()
@Resettable()
export class ProjectsService extends CollectionServiceBase<Project> {
  private readonly projectsApiService = inject(ProjectsApiService);
  private readonly licenseStorageService = inject(LicenseStorageService);
  private readonly permissionsService = inject(PermissionsService);

  constructor() {
    super(inject(ProjectsDataService));
  }

  protected override fetchItemById$(itemId: number): Observable<Project> {
    return this.projectsApiService.getProjectById$(itemId);
  }

  protected override fetchItemsById$(itemIds: number[]): Observable<Project[]> {
    return this.projectsApiService.getProjectsByIds$(itemIds);
  }

  getProjectsByLicenseId$(licenseId: number): Observable<Project[]> {
    return this.getDataListItems$('Licenses', licenseId, {
      fetch: () => this.projectsApiService.getProjectListItemsByLicenseId$(licenseId).pipe(
        map(projects => {
          if (Array.isArray(projects)) {
            return projects.sort((projectA, projectB) => projectA.Name.localeCompare(projectB.Name));
          }
        })
      )
    });
  }

  getProjectsPageByLicenseId$(licenseId: number, filter: PageFilter, maxIncludes?: number): Observable<Page<Project>> {
    return this.projectsApiService.getProjectsPageByLicenseId$(licenseId, filter, maxIncludes);
  }

  getReviewProjectsPageByLicenseId$(licenseId: number, filter: PageFilter): Observable<Page<Project>> {
    return this.projectsApiService.getReviewProjectsPageByLicenseId$(licenseId, filter);
  }

  checkProjectById$(projectId: number): Observable<Project> {
    return this.projectsApiService.checkProjectById$(projectId).pipe(
      tap(project => this.addItems$({ [project.Id]: project }))
    );
  }

  getProjectListItemsByLicenseId$(licenseId: number): Observable<Project[]> {
    return this.projectsApiService.getProjectListItemsByLicenseId$(licenseId).pipe(
      tap(projects => this.addItems$(projects)),
      map(projects => {
        if (Array.isArray(projects)) {
          return projects.sort((projectA, projectB) => projectA.Name.localeCompare(projectB.Name));
        }
      })
    );
  }

  updateProject$(project: Project): Observable<void> {
    return this.projectsApiService.updateProject$(project).pipe(
      tap(() => this.updateItems$({ [project.Id]: project }))
    );
  }

  updateProjectTeamsAndUsers$(project: Project): Observable<void> {
    return this.projectsApiService.updateProjectTeamsAndUsers$(project).pipe(
      tap(() => this.updateItems$({ [project.Id]: project }))
    );
  }

  getProjectTeamsPage$(projectId: number, filter: PageFilter): Observable<Page<Team>> {
    return this.projectsApiService.getProjectTeamsPage$(projectId, filter);
  }

  getProjectLicenseUsersPage$(projectId: number, filter: PageFilter): Observable<Page<LicenseUser>> {
    return this.projectsApiService.getProjectLicenseUsersPage$(projectId, filter);
  }

  getProjectsList$(licenseId: number): Observable<Project[]> {
    return this.projectsApiService.getProjectsList$(licenseId).pipe(
      map(projects => {
        if (Array.isArray(projects)) {
          return projects.sort((projectA, projectB) => {
            return projectA.Name.localeCompare(projectB.Name);
          });
        }
      })
    );
  }

  getProjectReportScanStatus$(projectId: number, branchName?: string): Observable<ProjectReportScanStatusWithLatestNotificationId> {
    return this.projectsApiService.getProjectReportScanStatus$(projectId, branchName).pipe(
      switchMap(currentStatus => {
        return this.updateItems$({
          [projectId]: { ReportScanStatus: currentStatus.Status }
        }).pipe(
          map(() => currentStatus)
        );
      })
    );
  }

  analyzeProject$(projectId: number, branchName: string): Observable<MadCloudResult<void>> {
    return this.projectsApiService.analyzeProject$(projectId, branchName).pipe(
      tap(() => {
        this.updateItems$({
          [projectId]: {
            Id: projectId,
            ReportScanStatus: ProjectReportScanStatus.Queued
          }
        });
      })
    );
  }

  updateProjectStatus$(projectId: number, status: ProjectStatus): Observable<MadCloudResult<void>> {
    return this.projectsApiService.putProjectStatus$(projectId, status).pipe(
      tap(() => this.updateItems$({ [projectId]: { Status: status } }))
    );
  }

  deleteProjectLicenseUser$(projectId: number, licenseUserId: number): Observable<void> {
    return this.projectsApiService.deleteProjectLicenseUser$(projectId, licenseUserId);
  }

  deleteProjectTeam$(projectId: number, teamId: number): Observable<Project> {
    return this.projectsApiService.deleteProjectTeam$(projectId, teamId);
  }

  bulkDeleteProjects$(projectIds: number[], licenseId?: number): Observable<void> {
    return this.projectsApiService.bulkDeleteProjects$(projectIds).pipe(
      tap(() => {
        this.deleteItems$(projectIds);
        if (licenseId) {
          this.licenseStorageService.getItemById$(licenseId, { forceApiRequest: true }).pipe(
            first(licenseStorage => !!licenseStorage)
          ).subscribe();
        }
      })
    );
  }

  addTeamsToProject$(projectId: number, teamIds: number[]): Observable<MadCloudResult<void>> {
    return this.projectsApiService.addTeamsToProject$(projectId, teamIds);
  }

  addLicenseUsersToProject$(projectId: number, licenseUserIds: number[]): Observable<MadCloudResult<void>> {
    return this.projectsApiService.addLicenseUsersToProject$(projectId, licenseUserIds);
  }

  addLicenseUserToProjects$(licenseUserId: number, projectIds: number[]): Observable<void> {
    return this.projectsApiService.addLicenseUserToProjects$(licenseUserId, projectIds);
  }

  bulkUpdateProjectStatus$(status: ProjectStatus, projectIds: number[]): Observable<void> {
    return this.projectsApiService.bulkUpdateProjectStatus$(status, projectIds).pipe(
      tap(() => projectIds.forEach(projectId => this.updateItems$({ [projectId]: { Status: status } })))
    );
  }

  getProjectDependencies$(projectId: number): Observable<ProjectDependencies> {
    return this.projectsApiService.getProjectDependencies$(projectId);
  }

  getProjectLanguage$(projectId: number, commitId: string): Observable<string> {
    return this.projectsApiService.getProjectLanguage$(projectId, commitId);
  }

  getTargetLanguage$(projectId: number, commitId: string, targetPath: string): Observable<string> {
    return this.projectsApiService.getTargetLanguage$(projectId, commitId, targetPath);
  }

  getProjectArchivePath(projectId: number, commitId: string): string {
    return this.projectsApiService.getProjectArchivePath(projectId, commitId);
  }

  createProject$(licenseId: number, project: FormData): Observable<Project> {
    return this.projectsApiService.createProject$(licenseId, project);
  }

  validateProjectName$(licenseId: number, projectName: string): Observable<void> {
    return this.projectsApiService.validateProjectName$(licenseId, projectName);
  }

  getProjectTemplates$(): Observable<CreateProjectTemplate[]> {
    return this.projectsApiService.getProjectTemplates$();
  }
}
